/**
 * Phase 51 — Inventory Financial Summary Dashboard
 * Phase 52.1 — `scope` prop restricts aggregation + card set to Silver or
 * Gold. Pass `undefined` for the legacy "all-materials" view.
 */
import React, { useEffect, useMemo, useState } from "react";
import usmApi from "@/lib/usmApi";
import { Wallet, Coins, Gem, TrendingUp } from "lucide-react";

const TRY_FMT = new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const USD_FMT = new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const GRAM_FMT = new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Whitelists mirror the backend `_STATS_*_MATERIALS` lists — keep in sync.
export const GOLD_MATERIALS = ["8K GOLD", "14K GOLD", "18K GOLD", "22K GOLD", "24K GOLD"];
export const SILVER_MATERIALS = ["925 SILVER", "999 SILVER"];

function scopeToMaterials(scope) {
  if (scope === "silver") return SILVER_MATERIALS;
  if (scope === "gold") return GOLD_MATERIALS;
  if (scope === "watch") return WATCH_MATERIALS;
  return null;
}

export const WATCH_MATERIALS = ["STEEL WATCH", "GOLD WATCH", "SMART WATCH", "OTHER WATCH"];

export default function InventorySummary({ branchId, refreshKey, branchLabel, scope }) {
  const [data, setData] = useState(null);
  const [rate, setRate] = useState(null);           // TCMB USD sell (TRY per USD)
  const [rateUpdatedAt, setRateUpdatedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const scopedMaterials = useMemo(() => scopeToMaterials(scope), [scope]);

  // Fetch aggregation whenever branch, scope or refreshKey changes.
  useEffect(() => {
    let alive = true;
    setLoading(true); setErr("");
    const params = new URLSearchParams();
    if (branchId) params.set("branch_id", branchId);
    if (scopedMaterials) params.set("materials", scopedMaterials.join(","));
    const qs = params.toString() ? `?${params.toString()}` : "";
    usmApi.get(`/inventory/summary${qs}`)
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setErr("Özet yüklenemedi"); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [branchId, refreshKey, scopedMaterials]);

  // TCMB USD — reuse the existing public rates service (no new endpoint).
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
        const r = await fetch(`${base}/api/rates`, { headers: { Accept: "application/json" } });
        if (!alive || !r.ok) return;
        const d = await r.json();
        const usd = (d.currencies || []).find((c) => c.code === "USD");
        const value = usd?.forex_selling || usd?.display_sell || null;
        if (value && alive) {
          setRate(value);
          setRateUpdatedAt(usd?.updated_at || d.fetched_at || null);
        }
      } catch { /* offline — USD figures fall back to "—" */ }
    })();
    return () => { alive = false; };
  }, [refreshKey]);

  const usd = useMemo(() => {
    if (!rate || !data) return { purchase: null, sale: null };
    return {
      purchase: data.purchase_total_try == null ? null : data.purchase_total_try / rate,
      sale: data.sale_total_try == null ? null : data.sale_total_try / rate,
    };
  }, [rate, data]);

  const rateTimeLabel = useMemo(() => {
    if (!rateUpdatedAt) return null;
    try { return new Date(rateUpdatedAt).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" }); }
    catch { return null; }
  }, [rateUpdatedAt]);

  return (
    <section data-testid="inventory-summary" className="mb-4">
      <div className={"grid gap-3 grid-cols-1 md:grid-cols-2 " + (scope ? "lg:grid-cols-3" : "lg:grid-cols-4")}>
        <MoneyCard
          testid="summary-purchase"
          label="Toplam Alış Değeri"
          Icon={Wallet}
          try_={data?.purchase_total_try}
          usd={usd.purchase}
          hidden={data && data.purchase_total_try == null}
          loading={loading}
        />
        <MoneyCard
          testid="summary-sale"
          label="Toplam Satış Değeri"
          Icon={TrendingUp}
          try_={data?.sale_total_try}
          usd={usd.sale}
          loading={loading}
          accent
        />
        {scope !== "silver" && (
          <GramCard
            testid="summary-gold"
            label="Altın Toplam Gram"
            Icon={Coins}
            grams={data?.gold_grams}
            tone="gold"
            loading={loading}
          />
        )}
        {scope !== "gold" && (
          <GramCard
            testid="summary-silver"
            label="Gümüş Toplam Gram"
            Icon={Gem}
            grams={data?.silver_grams}
            tone="silver"
            loading={loading}
          />
        )}
      </div>

      {/* Footer — TCMB USD + branch scope */}
      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[10.5px] uppercase tracking-[0.18em] text-[#71717A]">
        {rate ? (
          <span data-testid="summary-tcmb">
            TCMB USD · <span className="font-mono text-[#0A0A0A]">{TRY_FMT.format(rate)}</span>
            {rateTimeLabel && <span className="ml-2 text-[#A8A29E]">Güncellendi {rateTimeLabel}</span>}
          </span>
        ) : (
          <span className="text-[#A8A29E]">TCMB USD kuru mevcut değil</span>
        )}
        <span className="text-[#A8A29E]">
          Kapsam: <span className="text-[#52525B]">{branchLabel || "Tüm şubeler"}</span>
        </span>
        {data && (
          <span className="ml-auto text-[#A8A29E]">
            {data.item_count} adet ürün · aktif + rezerve + tamir
          </span>
        )}
        {err && <span className="text-red-600 not-italic">{err}</span>}
      </div>
    </section>
  );
}

// ── Cards ────────────────────────────────────────────────────────────────

function MoneyCard({ testid, label, Icon, try_, usd, hidden, loading, accent }) {
  // Hidden = the caller does not have `pricing.view_cost`. We keep the card
  // slot visible but with a graceful "gizli" placeholder so the grid alignment
  // doesn't jump between different roles.
  const tryTxt = loading ? "…" : (try_ == null ? "—" : `₺${TRY_FMT.format(try_)}`);
  const usdTxt = loading ? "" : (usd == null ? "—" : `$${USD_FMT.format(usd)}`);
  return (
    <div
      data-testid={testid}
      className={
        "border p-4 flex items-start gap-3 " +
        (accent ? "border-[#C8A96A] bg-[#FBF5E8]" : "border-[#E7E5E4] bg-white")
      }
    >
      <div className={"p-2 " + (accent ? "text-[#8A6A2A]" : "text-[#71717A]")}>
        <Icon size={18} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[10.5px] uppercase tracking-[0.2em] text-[#71717A] mb-1">{label}</div>
        {hidden ? (
          <div className="font-finance text-lg text-[#A8A29E]" data-testid={`${testid}-hidden`}>Yetki yok</div>
        ) : (
          <>
            <div
              className={"font-finance leading-tight " + (accent ? "text-2xl text-[#0A0A0A]" : "text-xl text-[#0A0A0A]")}
              data-testid={`${testid}-try`}
            >
              {tryTxt}
            </div>
            {!loading && (
              <div className="mt-1 text-[12px] font-mono text-[#52525B]" data-testid={`${testid}-usd`}>
                {usdTxt}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function GramCard({ testid, label, Icon, grams, tone, loading }) {
  // Gold = warm gilt tone, Silver = cool graphite tone — visually separated so
  // an operator scanning the row never confuses the two precious metals.
  const cls = tone === "gold"
    ? "border-[#E4CB88] bg-[#FBF5E8] text-[#8A6A2A]"
    : "border-[#D6D3D1] bg-[#F5F5F4] text-[#3F3F46]";
  const txt = loading ? "…" : (grams == null ? "—" : `${GRAM_FMT.format(grams)} g`);
  return (
    <div data-testid={testid} className={"border p-4 flex items-start gap-3 " + cls}>
      <div className="p-2 opacity-80"><Icon size={18} /></div>
      <div className="min-w-0 flex-1">
        <div className="text-[10.5px] uppercase tracking-[0.2em] mb-1 opacity-75">{label}</div>
        <div className="font-finance text-xl leading-tight text-[#0A0A0A]" data-testid={`${testid}-value`}>
          {txt}
        </div>
      </div>
    </div>
  );
}
